import React, { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';

import { doRequest } from '@r1-frontend/do-request';
import { ResponseDto } from '@r1-frontend/api-domru/api-content/v1/geography/get-city-info';
import { createUnauthRequest, ICreateUnauthDealerRequest } from '@r1-frontend/api-domru/api-request/v1/dealers/create-unauth-req';

import { btnSizes, btnTypes } from '@r1-frontend/ui-react/components/buttons/baseButton';
import { PolicyNote } from '@r1-frontend/ui-react/components/PolicyNote';
import PopupResult, { EResultPopupStatus } from '@r1-frontend/ui-react/components/popups/popupResult';
import Select from '@r1-frontend/ui-react/components/select/select';
import { IndentContainer } from '@r1-frontend/ui-react/experimental/containers';

import { getCities } from '~/src/api/content/geography/get-all-cities';
import { DEALERS_OFFER_LINK, POLICY_PAGE_LINK } from '~/src/constants';

import * as ST from './styled';

export interface ISelectCityData {
    label: string | number,
    value: string,
}

interface IFieldData {
    value: string,
    isValid: boolean,
    clearInput: () => void,
}

interface IResultPopupData {
    isOpen: boolean,
    type?: EResultPopupStatus,
    title: string,
    text: string,
}

const defaultResultPopupState: IResultPopupData = {
    isOpen: false,
    title: '',
    text: '',
};

const emailRegExp = /^[a-z0-9._+-]+@[a-z0-9.-]+\.[a-z]{2,64}/i;
const dealerIdRegExp = /^\d+$/i;

const DealerForm = (): JSX.Element => {
    const [cities, setCities] = useState<ResponseDto[]>([]);
    const [citySelectOptions, setCitySelectOptions] = useState<ISelectCityData[]>([]);

    const [dealerName, setDealerName] = useState<IFieldData>();
    const [dealerId, setDealerId] = useState('');
    const [dealerMail, setDealerMail] = useState('');
    const [dealerPhone, setDealerPhone] = useState<IFieldData>();
    const [dealerCity, setDealerCity] = useState<ISelectCityData | null>(null);
    const [clientName, setClientName] = useState<IFieldData>();
    const [clientPhone, setClientPhone] = useState<IFieldData>();
    const [clientCity, setClientCity] = useState<ISelectCityData | null>(null);
    const [isValidEmail, setIsValidEmail] = useState(true);
    const [isValidDealerID, setIsValidDealerID] = useState(true);
    const [isValidForm, setIsValidForm] = useState(false);

    const [resultPopupData, setResultPopupData] = useState<IResultPopupData>(defaultResultPopupState);

    const fetchCities = useCallback(async() => {
        const allCities = await getCities();

        if (Array.isArray(allCities)) {
            setCities(allCities);

            const cityOptions = allCities.map((city: ResponseDto) => ({ label: city.name, value: `${city.city_id}` }));
            setCitySelectOptions(cityOptions);
        }
    }, []);

    const validateEmail = useCallback((event: React.FocusEvent<HTMLInputElement>) => {
        setIsValidEmail(emailRegExp.test(event.target.value));
    }, []);

    const validateDealerID = useCallback((event: React.FocusEvent<HTMLInputElement>) => {
        setIsValidDealerID(dealerIdRegExp.test(event.target.value));
    }, [dealerId]);

    const onInputHandler = useCallback(async(event, _setState: Dispatch<SetStateAction<string>>) => {
        const value = event.target.value;
        _setState(value || '');
    }, []);

    const onInputSimpleHandler = useCallback((inputValue: IFieldData, _setState: Dispatch<SetStateAction<IFieldData | undefined>>) => {
        _setState(inputValue);
    }, []);

    const onSelectHandler = useCallback((selectValue: ISelectCityData, _setState: Dispatch<SetStateAction<ISelectCityData | null>>) => {
        _setState(selectValue);
    }, []);

    const onSubmitHandler = useCallback(async() => {
        const _data: ICreateUnauthDealerRequest = {
            dealerName: dealerName?.value || '',
            dealerId: dealerId || '',
            dealerMail: dealerMail || '',
            dealerPhone: dealerPhone?.value || '',
            dlrCityId: dealerCity?.value || '',
            clientName: clientName?.value || '',
            clientPhone: clientPhone?.value || '',
            clientCityId: clientCity?.value || '',
        };

        const providerId = clientCity && cities.find(city => city.city_id === +clientCity.value)?.provider_id;

        try {
            if (!providerId) {
                throw new Error();
            }
            await doRequest.apiRequest<unknown>(
                createUnauthRequest(providerId, _data),
            );

            setResultPopupData({
                isOpen: true,
                type: EResultPopupStatus.SUCCESS,
                title: 'Спасибо, заявка принята!',
                text: 'Статус заявки и другая информация доступны в личном кабинете. ' +
                    'Если это первая заявка, смотрите электронную почту, там вы найдёте инструкцию и доступ к личному кабинету.',
            });

            dealerName?.clearInput();
            dealerPhone?.clearInput();
            clientName?.clearInput();
            clientPhone?.clearInput();

            setDealerName(undefined);
            setDealerId('');
            setDealerMail('');
            setDealerPhone(undefined);
            setDealerCity(null);
            setClientName(undefined);
            setClientPhone(undefined);
            setClientCity(null);
        } catch (error) {
            setResultPopupData({
                isOpen: true,
                type: EResultPopupStatus.ERROR,
                title: 'Ошибка',
                text: 'Не удалось отправить заявку. Пожалуйста, попробуйте еще раз.',
            });
        }
    }, [cities, clientCity, clientName, clientPhone, dealerCity, dealerId, dealerMail, dealerName, dealerPhone]);

    const onCloseResultPopup = useCallback(() => {
        setResultPopupData(defaultResultPopupState);
    }, []);

    useEffect(() => {
        if (!cities.length) {
            fetchCities();
        }
    }, [cities, fetchCities]);

    useEffect(() => {
        setIsValidForm(
            !!(
                dealerName?.isValid &&
                !!dealerId &&
                isValidDealerID &&
                !!dealerMail &&
                isValidEmail &&
                dealerPhone?.isValid &&
                !!dealerCity?.value &&
                clientName?.isValid &&
                clientPhone?.isValid &&
                !!clientCity?.value
            ),
        );
    }, [
        dealerName,
        dealerId,
        isValidEmail,
        dealerPhone,
        dealerCity,
        clientName,
        clientPhone,
        clientCity,
    ]);

    const submitText = 'Оформить заявку';
    const dealersOffer = <>
        &nbsp;и&nbsp;
        <a
            href={DEALERS_OFFER_LINK}
            target='_blank'
            rel='noreferrer nofollow noopener'
            data-test={'linkAgree'}
        >
            <b>Договором оферты</b>
        </a>
    </>;

    return (
        <>
            <ST.FormWrapper>
                <ST.BlockTitle>Введите ваши данные</ST.BlockTitle>
                <ST.BlockSubtitle>Или просто <a href='https://dealers.dom.ru' target='_blank' rel='noreferrer'>войдите</a> в личный кабинет</ST.BlockSubtitle>
                <ST.InputDataBlock>
                    <ST.InputDealerName
                        name={'dealer-name'}
                        placeholder={'Имя'}
                        onChange={(value: IFieldData) => onInputSimpleHandler(value, setDealerName)}
                        required={true}
                        data-test='dealer-name'
                    />
                    <ST.InputDealerId
                        inputType={'text'}
                        placeholder={'ID'}
                        value={dealerId}
                        isNotValid={!isValidDealerID}
                        onFocus={validateDealerID}
                        onBlur={validateDealerID}
                        onInput={validateDealerID}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => onInputHandler(event, setDealerId)}
                        data-test='dealer-id'
                    />
                    <ST.InputDealerMail
                        inputType={'email'}
                        placeholder={'Почта'}
                        value={dealerMail}
                        isNotValid={!isValidEmail}
                        onFocus={validateEmail}
                        onBlur={validateEmail}
                        onInput={validateEmail}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => onInputHandler(event, setDealerMail)}
                        required={true}
                        data-test='dealer-mail'
                    />
                    <ST.InputDealerPhone
                        name={'dealer-phone'}
                        placeholder={'Телефон'}
                        onChange={(value: IFieldData) => onInputSimpleHandler(value, setDealerPhone)}
                        required={true}
                        data-test='dealer-phone'
                    />
                    <ST.DealerCitySelectWrapper>
                        <Select
                            value={dealerCity}
                            placeholder={'Город'}
                            options={citySelectOptions}
                            onChange={(value: ISelectCityData) => onSelectHandler(value, setDealerCity)}
                            // @ts-ignore
                            required={true}
                            isSearchable={true}
                            data-test='dealer-city'
                        />
                    </ST.DealerCitySelectWrapper>
                </ST.InputDataBlock>

                <ST.BlockTitle>Данные клиента</ST.BlockTitle>
                <ST.InputDataBlock>
                    <ST.InputClientName
                        name={'client-name'}
                        placeholder={'Имя клиента'}
                        onChange={(value: IFieldData) => onInputSimpleHandler(value, setClientName)}
                        required={true}
                        data-test='client-name'
                    />
                    <ST.InputClientPhone
                        name={'client-phone'}
                        placeholder={'Телефон клиента'}
                        onChange={(value: IFieldData) => onInputSimpleHandler(value, setClientPhone)}
                        required={true}
                        data-test='client-phone'
                    />
                    <ST.ClientCitySelectWrapper>
                        <Select
                            value={clientCity}
                            placeholder={'Город клиента'}
                            options={citySelectOptions}
                            onChange={(value: ISelectCityData) => onSelectHandler(value, setClientCity)}
                            // @ts-ignore
                            required={true}
                            isSearchable={true}
                            data-test='client-city'
                        />
                    </ST.ClientCitySelectWrapper>
                </ST.InputDataBlock>

                <ST.PoliciesBlock>
                    <IndentContainer $united={[0, 0, 12, 0]}>
                        <PolicyNote
                            data-test='form-agree'
                            buttonText={submitText}
                            agreeUrl={POLICY_PAGE_LINK}
                        >
                            {dealersOffer}
                        </PolicyNote>
                    </IndentContainer>
                </ST.PoliciesBlock>

                <ST.ActionBlock>
                    <ST.SubmitButton
                        size={btnSizes.LARGE}
                        type={btnTypes.ACCENT}
                        text={submitText}
                        onClick={onSubmitHandler}
                        disabled={!isValidForm}
                        data-test='form-submit'
                    />
                </ST.ActionBlock>
            </ST.FormWrapper>
            <PopupResult
                {...resultPopupData}
                onClose={onCloseResultPopup}
                data-test='form-result'
            />
        </>
    );
};

export default DealerForm;
